import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Users from './pages/Users'
import Organizations from './pages/Organizations'
import Projects from './pages/Projects'
import Members from './pages/Members'
import Invitations from './pages/Invitations'
import GuestTours from './pages/GuestTours'
import Billing from './pages/Billing'
import Notifications from './pages/Notifications'
import SystemHealth from './pages/SystemHealth'
import Analytics from './pages/Analytics'
import Settings from './pages/Settings'
import DiscountCodes from './pages/DiscountCodes'
import QueueMonitoring from './pages/QueueMonitoring'
import ExpansionRequests from './pages/ExpansionDeliverables'
import DatabaseSettings from './pages/DatabaseSettings'
import Onboarding from './pages/Onboarding'
import TrialAnalytics from './pages/TrialAnalytics'
import CheckAnalytics from './pages/CheckAnalytics'
import MotiocompAnalytics from './pages/MotiocompAnalytics'
import ClientProposals from './pages/ClientProposals'
import DevHandoffs from './pages/DevHandoffs'
import Integrations from './pages/Integrations'
import AiScout from './pages/AiScout'
import MotionodeAi from './pages/MotionodeAi'
import ModelPerformance from './pages/ModelPerformance'
import MoEvals from './pages/MoEvals'
import Metrics from './pages/Metrics'
import Login from './pages/Login'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from './store'
import { setCredentials, logout } from './store/slices/authSlice'
import { useVerifyTokenQuery } from './store/api/authApi'

function App() {
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch()
  
  // Skip verification if no token exists
  const { data: verifyData, error: verifyError, isLoading: isVerifying } = useVerifyTokenQuery(undefined, {
    skip: !token
  })

  useEffect(() => {
    if (token && verifyData?.success) {
      dispatch(setCredentials({
        user: verifyData.data.user,
        token: token
      }))
    } else if (token && verifyError) {
      // Token is invalid, clear it
      dispatch(logout())
    }
  }, [verifyData, verifyError, token, dispatch])

  // Show loading while verifying token
  if (token && isVerifying) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
            } 
          />
          <Route 
            path="/*" 
            element={
              isAuthenticated ? (
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/organizations" element={<Organizations />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/members" element={<Members />} />
                    <Route path="/invitations" element={<Invitations />} />
                    <Route path="/guest-tours" element={<GuestTours />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/discount-codes" element={<DiscountCodes />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/health" element={<SystemHealth />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/queue-monitoring" element={<QueueMonitoring />} />
                    <Route path="/expansion-requests" element={<ExpansionRequests />} />
                    <Route path="/database" element={<DatabaseSettings />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/trial-analytics" element={<TrialAnalytics />} />
                    <Route path="/check-analytics" element={<CheckAnalytics />} />
                    <Route path="/motiocomp-analytics" element={<MotiocompAnalytics />} />
                    <Route path="/client-proposals" element={<ClientProposals />} />
                    <Route path="/dev-handoffs" element={<DevHandoffs />} />
                    <Route path="/integrations" element={<Integrations />} />
                    <Route path="/ai-scout" element={<AiScout />} />
                    <Route path="/motionode-ai" element={<MotionodeAi />} />
                    <Route path="/model-performance" element={<ModelPerformance />} />
                    <Route path="/mo_evals" element={<MoEvals />} />
                    <Route path="/mo-evals" element={<MoEvals />} />
                    <Route path="/metrics" element={<Metrics />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </Router>
      
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            style: {
              background: '#10b981',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#ef4444',
            },
          },
        }}
      />
    </div>
  )
}

export default App 